import React, { FunctionComponent, useEffect, useState } from 'react'
import * as yup from 'yup'
import useFormal from '@kevinwolf/formal-web'
import { SubmitButton } from 'Elements/SubmitButton'
import Grid from '@material-ui/core/Grid'
import { useReservations } from 'components/views/reservations/ReservationsProvider'
import { parseDateTimeField, yupDateTimeField } from 'utils'
import SelectField from 'Elements/SelectField'
import { IReservationsListZoneData, useApi } from 'components/providers/ApiProvider'
import { useTranslation } from 'react-i18next'
import { PlateNumberField } from 'Elements/PlateNumberField'
import { TextField } from 'Elements/TextField'
import { DateTimeField } from 'Elements/DateTimeField'
import SearchIcon from '@material-ui/icons/Search'

export type Props = {}
export type Functions = {}
export type AllProps = Props & Functions

const Toolbar: FunctionComponent<AllProps> = ({}) => {
  const api = useApi()
  const { t, i18n } = useTranslation()
  const { filter, listLoading, onFilter } = useReservations()

  const [zones, setZones] = useState<IReservationsListZoneData[]>([])

  useEffect(() => {
    api.getReservationsListZones()
      .then(data => {
        setZones(data.zones)
      })
      .catch(() => {
      })
  }, [])

  const schema = yup.object().shape({
    zone: yup.number(),
    reservationType: yup.number(),
    plateNumber: yup.string(),
    client: yup.string().trim(),
    createdFrom: yupDateTimeField(),
    createdTill: yupDateTimeField(),
  })

  const formal = useFormal(filter, {
    schema,
    onSubmit: async values => {
      if (listLoading) {
        return
      }

      onFilter(schema.cast(values))
    },
  })

  return (
    <>
      <form {...formal.getFormProps()}>
        <Grid container spacing={1} style={{ padding: '16px 0' }}>
          <SelectField
            GridProps={{ xs: 6, md: 3 }}
            label={t('zone')}
            optionsCustom={[
              {
                label: t('allZones'),
                id: 0,
              },
              ...zones.sort((a, b) => a.code.localeCompare(b.code)).map(zone => {
                const localization = zone.localizations.find(l => l.language == i18n.language)

                return {
                  label: `${zone.code}${localization && localization.name ? ` ${localization.name}` : ''}`,
                  id: zone.id,
                }
              }),
            ]}
            size="small"
            {...formal.getFieldProps('zone')}
            onChange={e => {
              formal.change('zone', e.target.value)
              formal.change('reservationType', 0)
            }}
          />
          <SelectField
            GridProps={{ xs: 6, md: 3 }}
            label={t('type')}
            optionsCustom={[
              {
                label: t('allTypes'),
                id: 0,
              },
              ...((zones.find(z => z.id == formal.values.zone) || {}).reservation_types || []).map(type => ({
                label: type.name,
                id: type.id,
              })),
            ]}
            size="small"
            {...formal.getFieldProps('reservationType')}
          />
          <PlateNumberField
            GridProps={{ xs: 6, md: 3 }}
            clearable={true}
            size="small"
            {...formal.getFieldProps('plateNumber')}
          />
          <TextField
            GridProps={{ xs: 6, md: 3 }}
            label={t('client')}
            size="small"
            {...formal.getFieldProps('client')}
          />
          <DateTimeField
            GridProps={{ xs: 12, md: true }}
            label={`${t('reservedAt')} - ${t('from')}`}
            size="small"
            {...formal.getFieldProps('createdFrom')}
            initialFocusedDate={(new Date).setHours(0, 0, 0, 0)}
            {
              ...(formal.values.createdTill && { maxDate: parseDateTimeField(formal.values.createdTill) })
            }
            clearable
          />
          <DateTimeField
            GridProps={{ xs: 12, md: true }}
            label={`${t('reservedAt')} - ${t('till')}`}
            size="small"
            {...formal.getFieldProps('createdTill')}
            initialFocusedDate={(new Date).setHours(23, 59, 59, 99)}
            {
              ...(formal.values.createdFrom && { minDate: parseDateTimeField(formal.values.createdFrom) })
            }
            clearable
          />
          <SubmitButton
            GridProps={{ xs: 12, md: 'auto' }}
            buttonText={t('search')}
            startIcon={<SearchIcon />}
            disabled={listLoading}
          />
        </Grid>
      </form>
    </>
  )
}

export default Toolbar
